<template>
  <Layout>
    <div class="w-full bg-gray-100 py-3">
      <div class="max-w-6xl mx-auto px-4 lg:px-8">
        <ol class="flex items-center text-sm text-gray-500">
          <li>
            <router-link to="/" class="hover:text-primary">Home</router-link>
          </li>
          <li class="before:content-['/'] before:mx-2 text-gray-800">About Us</li>
        </ol>
      </div>
    </div>

    <div class="max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <div class="flex flex-col lg:flex-row gap-8">
        <div class="flex-1">
          <h2 class="text-2xl lg:text-4xl font-bold mb-4">Berlin Shelves Company – Corporate Profile</h2>
        </div>
        <div class="flex-1">
          <img :src="aboutMainImage" alt="" class="w-full h-auto rounded-lg shadow-md block">
        </div>
      </div>

      <p class="text-lg leading-7 text-left mt-3.5">
        Founded in 2018, Berlin Shelves Company is a premier, globally recognized leader in the commercial equipment industry. We specialize in the end-to-end integration of research and development (R&D), precision manufacturing, global export, and professional installation services.
      </p>
      <p class="text-lg leading-7 text-left mt-3.5">
        As a highly decorated industry pioneer, the company has been honored with prestigious accolades, including "Superior Provider in the Retail Industry" and "AAA-Grade Enterprise for Credit and Quality Integrity." Today, Berlin Shelves stands as a trusted international brand, celebrated for excellence by clients worldwide.
      </p>
    </div>

    <hr class="border-t border-gray-200 my-2 mx-4 lg:mx-8">

    <div class="max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <h2 class="text-2xl lg:text-4xl font-bold mb-4">World-Class Manufacturing & Scale</h2>
      <p class="text-lg leading-7 text-left mt-3.5">
        Our operational footprint is engineered to support global demand with uncompromising quality:
      </p>
      <ul class="list-disc text-lg mt-4 ml-8">
        <li class="mb-2.5 leading-relaxed">
          <strong class="font-semibold">Expansive Showroom:</strong> A state-of-the-art, 1,000+ square meter product showroom designed to host international buyers from over 100 countries annually.
        </li>
        <li class="mb-2.5 leading-relaxed">
          <strong class="font-semibold">Production Powerhouse:</strong> Over 20,000 square meters of advanced manufacturing and warehousing facilities across four specialized factories.
        </li>
        <li class="mb-2.5 leading-relaxed">
          <strong class="font-semibold">Full-Cycle Infrastructure:</strong> Dedicated, in-house workshops covering raw materials, heavy-duty welding, precision sheet metal, wire fabrication, automated coating, and secure packaging.
        </li>
      </ul>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div v-for="(manufacturing,index) in manufacturingList" :key="index" class="overflow-hidden">
          <img :src="manufacturing.cover" alt="" class="w-full h-auto object-contain block my-4 rounded-lg shadow-sm">
        </div>
      </div>
    </div>

    <hr class="border-t border-gray-200 my-2 mx-4 lg:mx-8">

    <div class="max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <h2 class="text-2xl lg:text-4xl font-bold mb-4">Advanced Technological Edge</h2>
      <p class="text-lg leading-7 text-left mt-3.5">
        To ensure maximum market competitiveness in both pricing and structural integrity, our facilities are equipped with cutting-edge, automated machinery, including:
      </p>
      <ul class="list-disc text-lg mt-4 ml-8">
        <li class="mb-2.5 leading-relaxed">Fiber optic laser cutting systems & CNC wire drawing machines</li>
        <li class="mb-2.5 leading-relaxed">Fully automatic pipe-bending and cutting lines</li>
        <li class="mb-2.5 leading-relaxed">24 high-capacity punching stations (ranging from 16 to 120 tons)</li>
        <li class="mb-2.5 leading-relaxed">Advanced robotic welding cells and 14-head gantry welding workstations</li>
        <li class="mb-2.5 leading-relaxed">Eco-friendly, fully automatic electrostatic powder coating and electroplating lines</li>
      </ul>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div v-for="(image,index) in techEdgeImages" :key="index" class="overflow-hidden rounded-lg shadow-sm group cursor-pointer">
          <div class="aspect-[16/10] overflow-hidden">
            <img :src="image" alt="" class="w-full h-full object-cover block transition-transform duration-300 group-hover:scale-105 group-hover:shadow-md">
          </div>
        </div>
      </div>
    </div>

    <hr class="border-t border-gray-200 my-2 mx-4 lg:mx-8">

    <div class="max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <h2 class="text-2xl lg:text-4xl font-bold mb-4">Comprehensive Product Portfolio</h2>
      <p class="text-lg leading-7 text-left mt-3.5">
        We deliver smart, space-optimizing solutions across diverse sectors, including retail logistics, apparel, pharmaceuticals, groceries, and industrial warehousing:
      </p>
      <ul class="list-disc text-lg mt-4 ml-8">
        <li class="mb-2.5 leading-relaxed">Commercial Retail Solutions: Premium supermarket shelving, metal-and-wood hybrid display racks, and checkout/supporting retail fixtures.</li>
        <li class="mb-2.5 leading-relaxed">Industrial Warehousing Systems: Light, medium, and heavy-duty adjustable pallet racks, mezzanine floors, and structural steel platforms.</li>
        <li class="mb-2.5 leading-relaxed">Boutique & Display Cabinets: High-end glass display cases and specialized showroom fixtures.</li>
        <li class="mb-2.5 leading-relaxed">Logistics & Material Handling: Automated conveyor systems, assembly lines, hydraulic lifting platforms, and pallet trucks.</li>
        <li class="mb-2.5 leading-relaxed">Commercial Refrigeration: Fresh-food display cases, commercial freezers, and cold storage engineering.</li>
      </ul>

      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div v-for="(portfolio,index) in portfolioList" :key="index" class="overflow-hidden">
          <img :src="portfolio.cover" alt="" class="w-full h-auto object-contain block my-4 rounded-lg shadow-sm">
        </div>
      </div>
    </div>

    <hr class="border-t border-gray-200 my-2 mx-4 lg:mx-8">

    <div class="max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <h2 class="text-2xl lg:text-4xl font-bold mb-4">Global Trust & Certified Quality</h2>
      <p class="text-lg leading-7 text-left mt-3.5">
        Berlin Shelves is a verified TUV Gold Supplier with strict adherence to international standards, holding ISO9000 Quality Management and CE Certifications.
      </p>
      <p class="text-lg leading-7 text-left mt-3.5">
        Our commitment to flawless execution has made us the long-term partner of choice for world-renowned multinational retail giants, including Walmart, Miniso, and Costco. With products exported to over 100 countries, our annual export volume proudly surpassed the $50 million milestone.
      </p>
      <p class="text-lg leading-7 text-left mt-3.5">
        Our Vision: Led by a founding team with over 20 years of sheet metal engineering and brand design expertise, we live by a simple guiding principle: "Expanding your business is our ultimate desire." We don't just sell shelves; we maximize the value of commercial space for global brands.
      </p>

      <div class="grid grid-cols-1 gap-4 mt-4">
        <div v-for="(certified,index) in certifiedList" :key="index" class="overflow-hidden">
          <img :src="certified.cover" alt="" class="w-full h-auto object-contain block my-4 rounded-lg shadow-sm">
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/components/common/CommonLayout";
import { getImageUrl } from '@/utils/image';

export default {
  name: "AboutView",
  components: {Layout},
  data() {
    return {
      manufacturingList: [],
      portfolioList: [],
      certifiedList: [],
      companyInfo: {},
      designers: [],
      aboutMainImage: getImageUrl('about/bld-aboutus.jpg'),
      missionImages: {
        f01: getImageUrl('about/f01.jpg'),
        f02: getImageUrl('about/f02.jpg'),
        f03: getImageUrl('about/f03.jpg'),
        f04: getImageUrl('about/f04.jpg')
      },
      techEdgeImages: [
        getImageUrl('about/tech/edge-1.jpg'),
        getImageUrl('about/tech/edge-2.jpg'),
        getImageUrl('about/tech/edge-3.jpg'),
        getImageUrl('about/tech/edge-4.jpg'),
        getImageUrl('about/tech/edge-5.jpg'),
        getImageUrl('about/tech/edge-6.jpg')
      ]
    }
  },
  mounted() {
    this.getCompanyInfo()
    this.getDesigners()
    this.getManufacturing()
    this.getPortfolio()
    this.getCertifications()
  },
  methods: {
    getCompanyInfo() {
      this.getRequest("/api/company-info").then(resp => {
        if (resp && resp.data) {
          this.companyInfo = resp.data.data || {}
        }
      })
    },
    getDesigners() {
      this.getRequest("/api/designers").then(resp => {
        if (resp && resp.data) {
          this.designers = resp.data.data || []
        }
      })
    },
    async getManufacturing() {
      const resp = await this.getRequest("/api/manufacturing")
      if (resp && resp.data) {
        this.manufacturingList = (resp.data.data || []).map(item => ({
          id: item.id,
          cover: item.imageUrl,
          title: item.title,
          desc: item.description
        }))
      }
    },
    async getPortfolio() {
      const resp = await this.getRequest("/api/portfolio")
      if (resp && resp.data) {
        this.portfolioList = (resp.data.data || []).map(item => ({
          id: item.id,
          cover: item.imageUrl
        }))
      }
    },
    async getCertifications() {
      const resp = await this.getRequest("/api/certifications")
      if (resp && resp.data) {
        this.certifiedList = (resp.data.data || []).map(item => ({
          id: item.id,
          cover: item.imageUrl
        }))
      }
    }
  }
}
</script>
